import { assertUnreachable } from '../types/util';
import { flatten } from '../util/array';
import { removeLeading } from '../util/transforms';
import { parsePhoneNumberFromString as parsePhoneNumber } from 'libphonenumber-js';

export const ACCOUNT_BIO_FIELDS: Array<{
  id: keyof AccountBioFields;
  tname: string;
  type?: 'phone';
}> = [
  { id: 'salutation', tname: 'salutation' },
  { id: 'academicDegree', tname: 'academicDegree' },
  { id: 'academicDegree2', tname: 'academicDegree2' },
  { id: 'namePrefix', tname: 'namePrefix' },
  { id: 'firstName', tname: 'firstName' },
  { id: 'lastName', tname: 'lastName' },
  { id: 'nameSuffix', tname: 'nameSuffix' },
  { id: 'position', tname: 'position' },
  { id: 'role', tname: 'role' },
  { id: 'division', tname: 'division' },
  { id: 'company', tname: 'company' },
  { id: 'workUrl', tname: 'workUrl' },
  { id: 'bioText', tname: 'bioText' },
  { id: 'mobilePhone', tname: 'mobilePhone.text', type: 'phone' },
  { id: 'workPhone', tname: 'workPhone.text', type: 'phone' },
  { id: 'homePhone', tname: 'homePhone.text', type: 'phone' },
  { id: 'hotlinePhone', tname: 'hotlinePhone.text', type: 'phone' },
  { id: 'assistantName', tname: 'assistantName.text' },
  { id: 'assistantPhone', tname: 'assistantPhone.text', type: 'phone' },
  { id: 'assistantEmail', tname: 'assistantEmail.text' },
  { id: 'assistantGeneric', tname: 'assistantGeneric.text' },
  { id: 'workFax', tname: 'workFax.text', type: 'phone' },
  { id: 'email', tname: 'email1' },
  { id: 'workEmail', tname: 'email2' },
  { id: 'workEmail2', tname: 'workEmail2' },
  { id: 'address1_addressee', tname: 'address.addressee' },
  { id: 'address1_addressLine1', tname: 'address.addressLine1' },
  { id: 'address1_addressLine2', tname: 'address.addressLine2' },
  { id: 'address1_postCode', tname: 'address.postCode' },
  { id: 'address1_city', tname: 'address.city' },
  { id: 'address1_country', tname: 'address.country' },
  { id: 'address1_label', tname: 'address.label' },
  { id: 'address2_addressee', tname: 'address.2.addressee' },
  { id: 'address2_addressLine1', tname: 'address.2.addressLine1' },
  { id: 'address2_addressLine2', tname: 'address.2.addressLine2' },
  { id: 'address2_postCode', tname: 'address.2.postCode' },
  { id: 'address2_city', tname: 'address.2.city' },
  { id: 'address2_country', tname: 'address.2.country' },
  { id: 'address2_label', tname: 'address.2.label' },
  { id: 'costCenter', tname: 'costCenter' },
  { id: 'internalNotes', tname: 'internalNotes' },
];

export type AccountBioPhoneFieldKeys =
  | 'mobilePhone'
  | 'workPhone'
  | 'homePhone'
  | 'hotlinePhone'
  | 'assistantPhone'
  | 'workFax';
export const isPhoneField = (field: string): field is AccountBioPhoneFieldKeys => {
  return ACCOUNT_BIO_FIELDS.filter(x => x.type === 'phone')
    .map(x => x.id)
    .some(x => x === field);
};

export type AccountBioFields = {
  salutation?: string | null;
  academicDegree?: string | null;
  academicDegree2?: string | null;
  namePrefix?: string | null;
  nameSuffix?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  role?: string | null;
  division?: string | null;
  company?: string | null;
  workUrl?: string | null;
  bioText?: string | null;
  mobilePhone?: string | null;
  workPhone?: string | null;
  homePhone?: string | null;
  hotlinePhone?: string | null;
  assistantName?: string | null;
  assistantPhone?: string | null;
  assistantEmail?: string | null;
  assistantGeneric?: string | null;
  workFax?: string | null;
  email?: string | null;
  workEmail?: string | null;
  workEmail2?: string | null;
  address1_addressee?: string | null;
  address1_addressLine1?: string | null;
  address1_addressLine2?: string | null;
  address1_postCode?: string | null;
  address1_city?: string | null;
  address1_country?: string | null;
  address1_label?: string | null;
  address2_addressee?: string | null;
  address2_addressLine1?: string | null;
  address2_addressLine2?: string | null;
  address2_postCode?: string | null;
  address2_city?: string | null;
  address2_country?: string | null;
  address2_label?: string | null;
  costCenter?: string | null;
  internalNotes?: string | null;
};

export type BioFieldsConfig = {
  hiddenFields: (
    | AccountHeaderFieldKeys
    | AccountContactDetaisBoxFieldKeys
    | AccountCustomBioTextBoxFieldKeys
    | AccountOtherFieldKeys
  )[];

  singleUserNonEditableFields: (
    | AccountHeaderFieldKeys
    | AccountContactDetaisBoxFieldKeys
    | AccountCustomBioTextBoxFieldKeys
    | AccountOtherFieldKeys
  )[];

  notAllowedCustomTextFields: (keyof AccountBioFields)[];
  autocompleteOptions: {
    [key: string]: string[];
  };
  sorting: {
    headerFields: AccountHeaderFieldKeys[];
    contactDetailsBoxFields: AccountContactDetaisBoxFieldKeys[];
    customBioTextBoxFields: AccountCustomBioTextBoxFieldKeys[];
    otherFields: AccountOtherFieldKeys[];
  };
  settings: {
    [key in AccountBioPhoneFieldKeys]?: PhoneNumberFormattingSettings;
  };
};

export type PhoneNumberFormattingSettings = {
  includeCountryCode: boolean;
  formattingMode: 'manual' | 'rule' | 'auto';
  formattingRules: PhoneNumberFormattingBlock[]; // relevant for manual mode
};
export type PhoneNumberFormattingBlock =
  | { type: 'digits'; amount: number } // a block of _amount_ digits
  | { type: 'separator'; value: string } // a separator string
  | { type: 'area-code' } // area code, must be first block
  | { type: 'rest' }; // any number of digits; can only be used once
export const PREDEFINED_PHONE_NUMBER_SEPARATORS = [
  { name: 'space', value: ' ' },
  { name: 'dot', value: '.' },
  { name: 'hyphen', value: '-' },
  { name: 'slash', value: '/' },
] as const;

export type AccountHeaderFieldKeys = keyof Pick<
  AccountBioFields,
  | 'salutation'
  | 'academicDegree'
  | 'academicDegree2'
  | 'namePrefix'
  | 'firstName'
  | 'lastName'
  | 'nameSuffix'
  | 'position'
  | 'role'
  | 'division'
  | 'company'
>;
export type AccountContactDetaisBoxFieldKeys =
  | keyof Pick<
      AccountBioFields,
      | 'mobilePhone'
      | 'workPhone'
      | 'homePhone'
      | 'hotlinePhone'
      | 'assistantName'
      | 'assistantPhone'
      | 'assistantEmail'
      | 'assistantGeneric'
      | 'workFax'
      | 'email'
      | 'workEmail'
      | 'workEmail2'
      | 'workUrl'
      | 'address1_addressee'
      | 'address1_addressLine1'
      | 'address1_addressLine2'
      | 'address1_postCode'
      | 'address1_city'
      | 'address1_country'
      | 'address1_label'
      | 'address2_addressee'
      | 'address2_addressLine1'
      | 'address2_addressLine2'
      | 'address2_postCode'
      | 'address2_city'
      | 'address2_country'
      | 'address2_label'
    >
  | 'defaultAddresses';

export type AccountCustomBioTextBoxFieldKeys = keyof Pick<AccountBioFields, 'bioText'>;
export type AccountOtherFieldKeys = keyof Pick<AccountBioFields, 'costCenter' | 'internalNotes'>;

export const ACCOUNT_BIO_FIELDS_KEYS = ACCOUNT_BIO_FIELDS.map(x => x.id) as Array<
  keyof AccountBioFields
>;

export const ACCOUNT_MULTILINE_FIELDS = [
  'nameSuffix',
  'company',
  'role',
  'division',
  'position',
  'bioText',
] as const;

export const FULL_NAME_FIELDS = ['namePrefix', 'firstName', 'lastName', 'nameSuffix'] as const;

export const ADDRESS1_FIELDS = [
  'address1_addressee',
  'address1_addressLine1',
  'address1_addressLine2',
  'address1_postCode',
  'address1_city',
  'address1_country',
  'address1_label',
] as const;

export const ADDRESS2_FIELDS = [
  'address2_addressee',
  'address2_addressLine1',
  'address2_addressLine2',
  'address2_postCode',
  'address2_city',
  'address2_country',
  'address2_label',
] as const;

export const ASSISTANT_FIELDS = [
  'assistantName',
  'assistantPhone',
  'assistantEmail',
  'assistantGeneric',
] as const;

const defaultHeaderFields: BioFieldsConfig['sorting']['headerFields'] = [
  'salutation',
  'academicDegree',
  'academicDegree2',
  ...FULL_NAME_FIELDS,
  'position',
  'role',
  'division',
  'company',
];
const defaultCustomBioTextBoxFields: BioFieldsConfig['sorting']['customBioTextBoxFields'] = [
  'bioText',
];
const defaultContactDetailsBoxFields: BioFieldsConfig['sorting']['contactDetailsBoxFields'] = [
  'mobilePhone',
  'workPhone',
  'homePhone',
  'hotlinePhone',
  ...ASSISTANT_FIELDS,
  'workFax',
  'email',
  'workEmail',
  'workEmail2',
  'workUrl',
];
const defaultOtherFields: BioFieldsConfig['sorting']['otherFields'] = [
  'costCenter',
  'internalNotes',
];

export const getDefaultSorting = (
  defaultAddressesPosition: 'before' | 'after' = 'after',
): BioFieldsConfig['sorting'] => {
  return {
    headerFields: defaultHeaderFields,
    contactDetailsBoxFields: [
      ...defaultContactDetailsBoxFields,
      ...(defaultAddressesPosition === 'before' ? (['defaultAddresses'] as const) : []),
      ...ADDRESS1_FIELDS,
      ...ADDRESS2_FIELDS,
      ...(defaultAddressesPosition === 'after' ? (['defaultAddresses'] as const) : []),
    ],
    customBioTextBoxFields: defaultCustomBioTextBoxFields,
    otherFields: defaultOtherFields,
  };
};

export const defaultAllBioAttributes = flatten(Object.values(getDefaultSorting()));

export const getDefaultPhoneNumberSettings = (): PhoneNumberFormattingSettings => ({
  includeCountryCode: true,
  formattingMode: 'manual',
  formattingRules: [],
});

export type GroupedFieldType =
  | AccountHeaderFieldKeys
  | AccountContactDetaisBoxFieldKeys
  | AccountCustomBioTextBoxFieldKeys
  | AccountOtherFieldKeys
  | 'fullName'
  | 'address1'
  | 'address2'
  | 'assistant';

//Groups fields into a single field if there are multiple fields in the group
//E.g firstName, lastName, namePrefix, nameSuffix => fullName
//Address1 fields => address1
//Address2 fields => address2
export const groupOne = (
  fields: GroupedFieldType[],
  groupName: 'fullName' | 'address1' | 'address2' | 'assistant',
): GroupedFieldType[] => {
  const groupFieldKeys = groupMap[groupName];

  const configuredGroupFieldKeys = groupFieldKeys.filter(x => fields.includes(x));

  if (configuredGroupFieldKeys.length === 0) {
    return fields;
  }

  const [firstGroupElement] = configuredGroupFieldKeys;
  const firstGroupElementIndex = fields.indexOf(firstGroupElement as keyof AccountBioFields);

  const fieldsWithoutGroupElements = fields.filter(x => !groupFieldKeys.some(y => y === x));
  return [
    ...fieldsWithoutGroupElements.slice(0, firstGroupElementIndex),
    groupName,
    ...fieldsWithoutGroupElements.slice(firstGroupElementIndex),
  ];
};

const groupMap = {
  fullName: FULL_NAME_FIELDS,
  address1: ADDRESS1_FIELDS,
  address2: ADDRESS2_FIELDS,
  assistant: ASSISTANT_FIELDS,
};

export const groupAccountBioFields = (
  fields: GroupedFieldType[],
  groupNames: ('fullName' | 'address1' | 'address2' | 'assistant')[],
): GroupedFieldType[] => {
  return groupNames.reduce((acc, groupName) => groupOne(acc, groupName), fields);
};

export const getSortingAfterVisibility = (
  bioFieldsConfig: BioFieldsConfig,
): BioFieldsConfig['sorting'] => {
  const hiddenFields = bioFieldsConfig.hiddenFields || [];
  const isFieldVisible = (field: (typeof hiddenFields)[number]) => !hiddenFields.includes(field);
  return {
    headerFields: bioFieldsConfig.sorting.headerFields.filter(isFieldVisible),
    contactDetailsBoxFields: bioFieldsConfig.sorting.contactDetailsBoxFields.filter(isFieldVisible),
    customBioTextBoxFields: bioFieldsConfig.sorting.customBioTextBoxFields.filter(isFieldVisible),
    otherFields: bioFieldsConfig.sorting.otherFields.filter(isFieldVisible),
  };
};

export const formatPhoneNumber = (
  rawNumber: string | null | undefined,
  config: PhoneNumberFormattingSettings | null | undefined,
): string => {
  if (!rawNumber) return '';
  const nr = removeLeading(rawNumber, '+');

  const { formattingMode, includeCountryCode, formattingRules } =
    config || getDefaultPhoneNumberSettings();

  switch (formattingMode) {
    case 'manual':
      return includeCountryCode ? '+' + nr : nr;
    case 'rule':
      return formatPhoneNumberByRules(nr, includeCountryCode, formattingRules);
    case 'auto':
      return formatPhoneNumberAutomatically(nr, includeCountryCode);
  }
};

const formatPhoneNumberByRules = (
  nr: string,
  _includeCountryCode: boolean,
  _formattingRules: PhoneNumberFormattingBlock[],
) => {
  const [countryCode, localPart] = getNumberParts(nr, _includeCountryCode);
  const digits = extractOnlyDigits(localPart);
  return (
    countryCode +
    applyRules(removeLeading(countryCode, '+').trim(), digits.split(''), _formattingRules)
  );
};
const getNumberParts = (nr: string, includeCountryCode: boolean): [string, string] => {
  if (!includeCountryCode) return ['', nr];
  const parsed = parsePhoneNumber('+' + nr);
  if (!parsed) return ['', nr];
  else return ['+' + parsed.countryCallingCode + ' ', parsed.nationalNumber];
};
const extractOnlyDigits = (nr: string) => {
  return nr.match(/\d/g)?.join('') || '';
};
const extractAreaCode = (nrWithCountryCode: string) => {
  // auto detect area code as the first block after the country code when auto formatting
  const autoFormat = parsePhoneNumber('+' + nrWithCountryCode)?.formatInternational();
  if (autoFormat && autoFormat.split(' ').length > 2) return autoFormat.split(' ')[1];
  else return undefined;
};
const isRulesApplicable = (
  countryCode: string,
  nrWithoutCountryCode: string,
  rules: PhoneNumberFormattingBlock[],
): boolean => {
  if (rules.length === 0) return false;

  // rule validation
  if (rules.filter(r => r.type === 'area-code').length > 1) return false;
  if (rules.findIndex(r => r.type === 'area-code') > 0) return false;
  if (rules.filter(r => r.type === 'rest').length > 1) return false;

  const digits = extractOnlyDigits(nrWithoutCountryCode);

  const withRest = rules.some(r => r.type === 'rest');
  const withAreaCode = rules.some(r => r.type === 'area-code');

  if (withAreaCode && !countryCode) return false;

  let areaCode: string | undefined = undefined;
  if (withAreaCode && countryCode) {
    areaCode = extractAreaCode(countryCode + nrWithoutCountryCode);
    if (!areaCode) return false;
  }

  const expectedDigitsAmount = rules.reduce((acc, rule) => {
    if (rule.type === 'digits') return acc + rule.amount;
    if (rule.type === 'area-code' && areaCode) return acc + areaCode.length;
    return acc;
  }, 0);
  if (withRest) return digits.length >= expectedDigitsAmount;
  else return digits.length === expectedDigitsAmount;
};
const applyRules = (countryCode: string, digits: string[], rules: PhoneNumberFormattingBlock[]) => {
  const initialUnformattedDigits = digits.join('');
  if (!isRulesApplicable(countryCode, digits.join(''), rules)) return initialUnformattedDigits;
  let out = '';
  let areaCode: string | undefined = undefined;
  let ruleIdx = 0;
  for (const rule of rules) {
    switch (rule.type) {
      case 'area-code':
        areaCode = extractAreaCode(countryCode + initialUnformattedDigits);
        if (!areaCode) return initialUnformattedDigits;
        digits.splice(0, areaCode.length);
        out += areaCode;
        break;
      case 'digits':
        out += digits.splice(0, rule.amount).join('');
        break;
      case 'separator':
        out += rule.value;
        break;
      case 'rest':
        out += digits
          .splice(
            0,
            rules
              .filter((_r, idx) => idx > ruleIdx)
              .reduce((acc, r) => {
                if (r.type === 'digits') return acc - r.amount;
                return acc;
              }, digits.length),
          )
          .join('');
        break;
      default:
        assertUnreachable(rule, 'Unhandled rule type');
    }
    ruleIdx++;
  }
  return out;
};

const formatPhoneNumberAutomatically = (nr: string, includeCountryCode: boolean) => {
  if (!includeCountryCode) return nr; // cannot automatically format without country code
  const parsed = parsePhoneNumber('+' + nr);
  if (!parsed) return nr;
  else return parsed.formatInternational();
};
